import { Card } from "@/components/ui/card";
import { LayerItem } from "@exibra/shared";
import React, { useState, useRef, useEffect } from "react";
import styles from "./layer-preview.module.css";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Image, Music, Square, Text, Video, GripVertical } from "lucide-react";
import { DraggableAttributes } from "@dnd-kit/core";

/**
 * The props for the LayerPreview component.
 */
interface LayerPreviewProps {
    /** The layer to preview */
    layer: LayerItem;

    /** Whether the layer is selected */
    isSelected?: boolean;

    /** The onSelect callback */
    onSelect: (id: string) => void;

    /** The onNameChange callback */
    onNameChange: (id: string, newName: string) => void;

    /** Optional drag handle props for DnD functionality */
    dragHandleProps?: {
        attributes?: DraggableAttributes;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        listeners?: Record<string, any>;
    };
}

/**
 * A preview of a layer in the template editor.
 */
export const LayerPreview: React.FC<LayerPreviewProps> = ({
    layer,
    isSelected,
    onSelect,
    onNameChange,
    dragHandleProps,
}) => {
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const [name, setName] = useState(layer.name);
    const inputRef = useRef<HTMLInputElement>(null);
    const longPressTimeoutRef = useRef<NodeJS.Timeout>();
    const [touchStartTime, setTouchStartTime] = useState<number>(0);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const handleDoubleClick = () => {
        setIsEditing(true);
    };

    const handleTouchStart = () => {
        setTouchStartTime(Date.now());
        longPressTimeoutRef.current = setTimeout(() => {
            setIsEditing(true);
        }, 500); // 500ms for long press
    };

    const handleTouchEnd = () => {
        const touchDuration = Date.now() - touchStartTime;
        if (longPressTimeoutRef.current) {
            clearTimeout(longPressTimeoutRef.current);
        }
        // If it was a short tap (less than 500ms), treat it as a click
        if (touchDuration < 500) {
            onSelect(layer.id);
        }
    };

    const handleNameSubmit = () => {
        setIsEditing(false);
        if (name.trim() !== layer.name) {
            onNameChange(layer.id, name.trim());
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleNameSubmit();
        } else if (e.key === "Escape") {
            setIsEditing(false);
            setName(layer.name);
        }
    };

    const handleBlur = () => {
        handleNameSubmit();
    };

    return (
        <Card
            className={cn(styles.layerPreview, isSelected && styles.selected)}
            onClick={() => onSelect(layer.id)}
            onDoubleClick={handleDoubleClick}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            <div className="flex flex-row gap-4 items-center">
                <div
                    className="flex flex-col gap-2 cursor-grab"
                    {...(dragHandleProps?.attributes || {})}
                    {...(dragHandleProps?.listeners || {})}
                >
                    <GripVertical className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="flex flex-col gap-2">
                    {layer.type === "text" && <Text className="w-4 h-4" />}
                    {layer.type === "image" && <Image className="w-4 h-4" />}
                    {layer.type === "video" && <Video className="w-4 h-4" />}
                    {layer.type === "audio" && <Music className="w-4 h-4" />}
                    {layer.type === "shape" && <Square className="w-4 h-4" />}
                </div>
                <div>
                    {isEditing ? (
                        <input
                            type="text"
                            ref={inputRef}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onBlur={handleBlur}
                            className={styles.nameInput}
                        />
                    ) : (
                        layer.name
                    )}

                    {layer.isPlaceholder && (
                        <div className={cn(styles.subtitle, "text-muted-foreground")}>
                            {t("templates.layerTypes.placeholder")}
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};
